/**
 * Portal request form — Step 71: one item's requester-visible form, rendered
 * by the SAME FieldRenderer the agent side uses (dynamic sections included)
 * but in portal mode (no agent-domain calls: person fields are type-a-name,
 * directory dropdowns arrive pre-resolved from the server). Submitting shows
 * a confirmation with the #SR- reference — and the requester is the SESSION,
 * never a picker.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, CircleUserRound, X } from 'lucide-react';
import { portalApi } from '../../features/portal/api';
import { CatalogueIcon } from '../../features/fields/catalogueIcons';
import { FieldRenderer } from '../../features/fields/FieldRenderer';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { portalFetch } from '../../lib/portalApi';
import { FileFieldInput } from '../../features/fields/FileFieldInput';
import { attachmentError, type FileFieldValue } from '@itsm/shared';
import { Skeleton } from '../../components/ui/skeleton';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';

export function PortalRequestPage() {
  const { id = '' } = useParams();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.portalItemForm(id),
    queryFn: () => portalApi.itemForm(id),
    enabled: !!id,
  });
  const [values, setValues] = useState<Record<string, unknown>>({});
  /**
   * Step 70 — seed the answers from the server-resolved prefill.
   *
   * Only fields the person has NOT already touched: re-applying on every
   * refetch would overwrite what they were typing, which is the one behaviour
   * worse than not prefilling at all.
   */
  useEffect(() => {
    const prefill = data?.prefill;
    if (!prefill) return;
    setValues((current) => {
      const next = { ...current };
      let changed = false;
      for (const [key, value] of Object.entries(prefill)) {
        const held = next[key];
        if (held === undefined || held === '') {
          next[key] = value;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [data?.prefill]);

  /** Step 72 — who the request is FOR, when that is not the person here. */
  const [forPerson, setForPerson] = useState<{ email: string; name: string } | null>(null);
  /** Step 73 — files chosen for this request. */
  const [files, setFiles] = useState<FileFieldValue[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState<{ id: string; ref: string } | null>(null);
  const dirty = !done && (Object.keys(values).length > 0 || !!forPerson || files.length > 0);
  useDirty(dirty);

  if (isError)
    return (
      <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-4">
        <p className="text-sm font-medium text-destructive">Could not load this service</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );
  if (isPending || !data)
    return (
      <div className="mx-auto max-w-2xl space-y-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );

  if (done) {
    return (
      <div className="mx-auto max-w-xl rounded-lg border bg-card p-8 text-center">
        <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
        <h1 className="text-lg font-semibold">Request received</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          <strong>{data.item.name}</strong> was raised with the reference{' '}
          <strong className="text-foreground">{done.ref}</strong>. A confirmation email is on its way.
        </p>
        <div className="mt-5 flex justify-center gap-2">
          <Link to={`/portal/requests/${done.id}`}>
            <Button>View the request</Button>
          </Link>
          <Link to="/portal/catalogue">
            <Button variant="outline">Raise another</Button>
          </Link>
        </div>
      </div>
    );
  }

  /**
   * Step 73 — the same rule the submit endpoint enforces, so a required
   * attachment disables the button rather than being refused after the fact.
   * The server is still the gate.
   */
  const fileProblem = attachmentError(
    {
      allowAttachments: !!data.item.allowAttachments,
      requireAttachment: !!data.item.requireAttachment,
    },
    files.length,
  );

  return (
    <div className="mx-auto max-w-2xl">
      <Link
        to="/portal/catalogue"
        className="mb-4 flex w-fit items-center gap-1.5 text-sm text-brand hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Back to catalogue
      </Link>
      <div className="rounded-lg border bg-card p-6">
        <div className="mb-1 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand/10 text-brand">
            <CatalogueIcon
              name={data.item.icon}
              attachmentId={data.item.iconAttachmentId}
              className="h-5 w-5"
            />
          </span>
          <h1 className="text-lg font-semibold">{data.item.name}</h1>
        </div>
        {data.item.description && (
          <p className="mb-3 text-sm text-muted-foreground">{data.item.description}</p>
        )}
        {/* Rich description + the cost/delivery the admin chose to publish
            (Step 65). Sanitised on WRITE, so rendering is plain. */}
        {data.item.descriptionHtml && (
          <div
            className="prose prose-sm dark:prose-invert mb-4 max-w-none overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: data.item.descriptionHtml }}
          />
        )}
        {(data.item.cost || data.item.deliveryTime) && (
          <div className="mb-5 flex flex-wrap gap-4 rounded-md border bg-muted/20 px-3 py-2 text-xs">
            {data.item.cost && (
              <span>
                <span className="text-muted-foreground">Cost: </span>
                <span className="font-medium">{data.item.cost}</span>
              </span>
            )}
            {data.item.deliveryTime && (
              <span>
                <span className="text-muted-foreground">Estimated delivery: </span>
                <span className="font-medium">{data.item.deliveryTime}</span>
              </span>
            )}
          </div>
        )}
        {data.item.allowOnBehalf && (
          <div className="mb-4 rounded-md border p-3">
            <RequestedFor value={forPerson} onChange={setForPerson} />
          </div>
        )}
        <FieldRenderer
          fields={data.fields}
          values={values}
          onChange={(k, v) => setValues((prev) => ({ ...prev, [k]: v }))}
          errors={errors}
          surface="portal"
        />
        {data.item.allowAttachments && (
          <div className="mt-4">
            <label className="mb-1.5 block text-sm font-medium">
              Attachments
              {data.item.requireAttachment && <span className="ml-1 text-destructive">*</span>}
            </label>
            <FileFieldInput value={files} onChange={setFiles} maxFiles={10} maxSizeMb={15} surface="portal" />
            {fileProblem && <p className="mt-1 text-xs text-destructive">{fileProblem}</p>}
          </div>
        )}
        {submitError && <p className="mt-3 text-sm text-destructive">{submitError}</p>}
        <div className="mt-6 flex justify-end border-t pt-4">
          <Button
            disabled={submitting || !!fileProblem}
            onClick={async () => {
              setSubmitting(true);
              setSubmitError(null);
              setErrors({});
              const res = await portalApi.submit(
                id,
                values,
                forPerson
                  ? { email: forPerson.email, ...(forPerson.name ? { name: forPerson.name } : {}) }
                  : undefined,
              );
              setSubmitting(false);
              if (!res.ok) {
                const details = (res.errorBody as { details?: Record<string, string> } | undefined)?.details;
                if (details) setErrors(details);
                setSubmitError(res.error ?? 'Something went wrong — try again.');
                return;
              }
              setValues({});
              setForPerson(null);
              setFiles([]);
              setDone({ id: res.body!.id, ref: res.body!.ref });
            }}
          >
            Place request
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * "Who is this for?" — Step 72 on the portal.
 *
 * The case the feature exists for happens HERE, not in the agent workspace: a
 * manager raising a starter form, an assistant ordering for a director. The
 * control is deliberately off by default — most requests are for the person
 * filling them in, and a picker that starts empty and required would make the
 * common path longer to serve the rare one.
 *
 * Search runs against `/api/portal/people`, never the agent directory
 * endpoints — the portal is its own auth domain and does not get to call
 * across.
 */
function RequestedFor({
  value,
  onChange,
}: {
  value: { email: string; name: string } | null;
  onChange: (v: { email: string; name: string } | null) => void;
}) {
  /**
   * URL state on purpose: a type-ahead that clears the moment somebody is
   * picked. It is not WHERE YOU ARE — restoring it from a link would drop a
   * reader into a half-typed search, which is the transient state the plan
   * says never reaches the URL.
   */
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ id: string; name: string | null; email: string }[]>([]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await portalFetch<{ entries: { id: string; name: string | null; email: string }[] }>(
          `/api/portal/people?q=${encodeURIComponent(term)}`,
        );
        setResults(res.entries);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  if (value)
    return (
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2 text-sm">
          <CircleUserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">
            For {value.name || value.email}
            <span className="text-muted-foreground"> — you'll be kept copied in</span>
          </span>
        </span>
        <button
          onClick={() => onChange(null)}
          aria-label="Request this for myself instead"
          className="rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );

  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
        Requesting this for someone else?
      </label>
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search for them by name or email — or leave blank if it's for you"
      />
      {results.length > 0 && (
        <div className="mt-1.5 space-y-1">
          {results.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                onChange({ email: p.email, name: p.name ?? '' });
                setQuery('');
              }}
              className="flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <CircleUserRound className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="truncate">
                {p.name ? `${p.name} · ` : ''}
                {p.email}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
